import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useBrand, useGroups, useMe, usePaging, usePublicConfig } from "../hooks.ts";
import { Badge, Button, Card, cx, ErrorText, Input, PageHeader, Pagination, Select, Spinner } from "../components/ui.tsx";
import { fmtDate, MemoryExplorer, type MemItem } from "../components/memory-map.tsx";

type Bank = { items: MemItem[]; total: number; truncated: boolean };

/**
 * Admin → Knowledge → Shared memory: everything in company memory (what people's assistants shared with
 * their group or the company, and synced documents), to review, remove or re-scope. What promotion kept
 * private is never shown, only counted: those memories are the person's own.
 */
export function SharedMemoryPage() {
  const brand = useBrand();
  const qc = useQueryClient();
  const groups = useGroups();
  const [audience, setAudience] = useState("all");
  const memory = useQuery({ queryKey: ["memory"], queryFn: () => unwrap(api.admin.memory.$get()) });
  const bank = useQuery({
    queryKey: ["memory", "bank", audience],
    queryFn: () => unwrap(api.admin.memory.bank.$get({ query: { audience } })) as Promise<Bank>,
    placeholderData: keepPreviousData,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["memory"] });
  const run = useMutation({ mutationFn: () => unwrap(api.admin.memory.run.$post()), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: (id: number) => unwrap(api.admin.memory.promotions[":id"].$delete({ param: { id: String(id) } })), onSuccess: refresh });
  const rescope = useMutation({
    mutationFn: ({ id, to }: { id: number; to: "group" | "company" }) => unwrap(api.admin.memory.promotions[":id"].audience.$put({ param: { id: String(id) }, json: { audience: to } })),
    onSuccess: refresh,
  });

  const d = memory.data;
  const canRun = !!d?.settings.promotionEnabled && !!d.engineConfigured;
  const actions = (i: MemItem) =>
    i.promotionId ? (
      <>
        {i.promotion && (
          <button
            disabled={rescope.isPending}
            onClick={() =>
              confirm(i.promotion!.audience === "company" ? "Keep this to the group of the person it came from?" : "Share this with everyone in the company?") &&
              rescope.mutate({ id: i.promotionId!, to: i.promotion!.audience === "company" ? "group" : "company" })
            }
            className="text-[var(--color-accent)] hover:underline"
          >
            {i.promotion.audience === "company" ? "Keep to their group" : "Share with everyone"}
          </button>
        )}
        <button disabled={revoke.isPending} onClick={() => confirm("Remove this from company memory? Assistants stop recalling it.") && revoke.mutate(i.promotionId!)} className="text-red-700 hover:underline">
          Remove
        </button>
      </>
    ) : null;

  return (
    <>
      <PageHeader
        title="Shared memory"
        description="What people's assistants shared with their group or the whole company, and what knowledge sync added. Review it, remove what shouldn't be there, or change who can recall it."
        actions={
          <>
            {run.data && <span className="text-xs text-[var(--muted)]">{run.data.skipped ?? `Checked ${run.data.users} people · ${run.data.promoted} shared`}</span>}
            <Button disabled={!canRun || run.isPending} onClick={() => run.mutate()} title={canRun ? "Check new memories against the rules now, instead of within 10 minutes" : "Sharing is switched off, or the memory engine isn't set up"}>
              {run.isPending ? "Running…" : "Run now"}
            </Button>
            <MoreMenu />
          </>
        }
      />
      {d && !d.engineConfigured && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">The memory engine isn't connected to {brand.name} yet (missing system key), so nothing is shared.</div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-auto" aria-label="Whose view">
          <option value="all">All company memory</option>
          <option value="everyone">What everyone can recall</option>
          {groups.data?.map((g) => (
            <option key={g.id} value={g.id}>What {g.name} can recall</option>
          ))}
        </Select>
        {bank.data && <Badge>{bank.data.total.toLocaleString()} {bank.data.total === 1 ? "memory" : "memories"}</Badge>}
        {bank.data?.truncated && <span className="text-xs text-[var(--muted)]">Showing the newest {bank.data.items.length.toLocaleString()}.</span>}
      </div>
      <ErrorText error={run.error ?? revoke.error ?? rescope.error ?? memory.error} />
      {bank.isPending ? (
        <div className="grid place-items-center py-16"><Spinner /></div>
      ) : bank.error ? (
        <ErrorText error={bank.error} />
      ) : !bank.data.items.length ? (
        <Card className="px-6 py-10 text-center text-sm text-[var(--muted)]">Nothing here yet. Shared memories and synced documents show up here.</Card>
      ) : (
        <div className={cx(bank.isPlaceholderData && "opacity-60")}>
          <MemoryExplorer items={bank.data.items} scope="admin" views={["list", "topics", "map"]} actions={actions} />
        </div>
      )}

      <Decisions />
      <KeptPrivate />
    </>
  );
}

/**
 * Less-used things, out of the way: the memory engine's own (raw) inspector. It shows every person's private
 * memory, so only the super admin (the first person who signed in) gets it.
 */
function MoreMenu() {
  const cfg = usePublicConfig();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (!cfg.data?.knowledgeUrl || !me.data?.superAdmin) return null;
  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" aria-label="More" aria-expanded={open} onClick={() => setOpen(!open)}>
        ⋯
      </Button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-64 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1 shadow-lg">
          <a href={cfg.data.knowledgeUrl} target="_blank" rel="noopener" className="block rounded-md px-3 py-2 text-sm hover:bg-[var(--panel-2)]" onClick={() => setOpen(false)}>
            Raw inspector ↗
            <span className="block text-xs text-[var(--muted)]">The memory engine's own tool, for troubleshooting. It shows everyone's memory, private included; only you can open it.</span>
          </a>
        </div>
      )}
    </div>
  );
}

/** Recent sharing decisions: what was shared (the shared wording only) and what failed (the error only). */
function Decisions() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"promoted" | "error">("promoted");
  const paging = usePaging([tab], 10);
  const { page, pageSize, q } = paging;
  const list = useQuery({
    queryKey: ["memory", "promotions", tab, page, pageSize, q],
    queryFn: () => unwrap(api.admin.memory.promotions.$get({ query: { page: String(page), pageSize: String(pageSize), decision: tab, ...(q && { q }) } })),
    placeholderData: keepPreviousData,
  });
  useEffect(() => void paging.clamp(list.data?.total), [list.data?.total]); // eslint-disable-line react-hooks/exhaustive-deps
  const revoke = useMutation({
    mutationFn: (id: number) => unwrap(api.admin.memory.promotions[":id"].$delete({ param: { id: String(id) } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memory"] }),
  });
  return (
    <>
      <h2 className="mt-10 mb-1 text-base font-semibold">Sharing decisions</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">Each memory people's assistants shared, newest first, including ones since removed; and any the AI couldn't decide on.</p>
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 py-3">
          {(
            [
              ["promoted", "Shared"],
              ["error", "Errors"],
            ] as const
          ).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={cx("rounded-md px-3 py-1.5 text-sm", tab === id ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}>
              {label}
            </button>
          ))}
          <Input value={paging.search} onChange={(e) => paging.setSearch(e.target.value)} placeholder="Search by shared text, person or email…" className="ml-auto max-w-xs" />
        </div>
        <ErrorText error={revoke.error} />
        {list.isPending ? (
          <div className="grid place-items-center py-10"><Spinner /></div>
        ) : list.error ? (
          <div className="p-4"><ErrorText error={list.error} /></div>
        ) : !list.data.items.length ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--muted)]">{q ? "Nothing matches that search." : tab === "error" ? "No errors." : "Nothing shared yet."}</p>
        ) : (
          <ul className={cx(list.isPlaceholderData && "opacity-60")}>
            {list.data.items.map((p) => (
              <li key={p.id} className="flex flex-wrap items-start gap-3 border-b border-[var(--line)] px-4 py-3 last:border-0">
                <div className="min-w-0 flex-1 basis-60 text-sm">
                  {p.decision === "promoted" ? (
                    <div className={cx(p.revokedAt && "text-[var(--muted)] line-through")}>{p.sharedText}</div>
                  ) : (
                    <div>Couldn't decide: {p.reason || "no reason given"}. It is tried again on the next run.</div>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
                    <span>{fmtDate(p.createdAt)}</span>
                    <span>
                      {p.name} · {p.groupName ?? "no group"}
                    </span>
                    {p.decision === "promoted" && <Badge tone={p.audience === "company" ? "good" : "accent"}>{p.audience === "company" ? "Everyone" : `Only ${p.sharedGroupName ?? "a deleted group"}`}</Badge>}
                    {p.revokedAt && <span>removed by {p.revokedBy}</span>}
                  </div>
                </div>
                {p.decision === "promoted" && !p.revokedAt && (
                  <Button variant="danger" className="h-8" disabled={revoke.isPending} onClick={() => confirm("Remove this from company memory?") && revoke.mutate(p.id)}>
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <Pagination page={page} pageSize={pageSize} total={list.data?.total ?? 0} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={list.isFetching} />
      </Card>
    </>
  );
}

/** How much was kept private, per group and week: only counts, never the memories. */
function KeptPrivate() {
  const q = useQuery({ queryKey: ["memory", "kept-private"], queryFn: () => unwrap(api.admin.memory["kept-private"].$get()) });
  return (
    <>
      <h2 className="mt-10 mb-1 text-base font-semibold">Kept private</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">
        How many new memories the rules kept private, against how many were shared, over the last eight weeks. Only counts: what wasn't shared stays with the person, and this page never shows it.
      </p>
      <Card className="overflow-x-auto">
        {q.isPending ? (
          <div className="grid place-items-center py-8"><Spinner /></div>
        ) : q.error ? (
          <div className="p-4"><ErrorText error={q.error} /></div>
        ) : !q.data.weeks.length ? (
          <p className="px-4 py-6 text-sm text-[var(--muted)]">No memories were checked in the last eight weeks.</p>
        ) : (
          <table className="w-full min-w-[420px] text-sm">
            <thead className="bg-[var(--panel-2)] text-left text-xs text-[var(--muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Week of</th>
                <th className="px-4 py-2 font-medium">Group</th>
                <th className="px-4 py-2 text-right font-medium">Shared</th>
                <th className="px-4 py-2 text-right font-medium">Kept private</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {q.data.weeks.map((w) => (
                <tr key={`${w.week}-${w.groupName}`}>
                  <td className="px-4 py-2">{fmtDate(w.week)}</td>
                  <td className="px-4 py-2">{w.groupName ?? "No group"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.shared}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.kept}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
